import {useEffect, useRef} from 'react';
import {Platform, AppState} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {resetState} from '../redux/services/authSlice';
import {useSignOutMutation} from '../redux/services/ausersApi';
import {storeSetting} from '../Common/common';
import {RootState} from '../redux/store';

const useInactivityLogout = (timeout: number = 600000) => {
  const dispatch = useDispatch();
  const [signOut] = useSignOutMutation();
  const user = useSelector((state: RootState) => state.gdata.user);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appStateSubscriptionRef = useRef<any>(null);

  useEffect(() => {
    // Only set up timer if user is logged in
    if (!user) {
      // Clear any existing timer if user logs out
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const resetTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(async () => {
        try {
          // Call signout API
          await signOut({}).unwrap();
        } catch (error) {
          // Continue with logout even if API call fails
          console.error('Signout API error on auto-logout:', error);
        } finally {
          // Clear settings
          await storeSetting('defoutlet', '0');

          // Reset auth state
          dispatch(resetState());
        }
      }, timeout);
    };

    const handleAppStateChange = (nextAppState: string) => {
      if (nextAppState === 'active') {
        resetTimer();
      }
    };

    if (Platform.OS === 'web') {
      // Web platform: use window events
      const activityEvents = [
        'mousedown',
        'mousemove',
        'keydown',
        'scroll',
        'touchstart',
      ];

      // Set up event listeners for activity
      activityEvents.forEach(event => {
        window.addEventListener(event, resetTimer, {passive: true});
      });

      // Set the initial timer
      resetTimer();

      // Clean up event listeners on component unmount
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        activityEvents.forEach(event => {
          window.removeEventListener(event, resetTimer);
        });
      };
    } else {
      // React Native: use AppState
      // AppState handles when app comes to foreground/background
      // The timer will also reset when this component re-renders (which happens on navigation)
      appStateSubscriptionRef.current = AppState.addEventListener(
        'change',
        handleAppStateChange,
      );

      // Set the initial timer
      resetTimer();

      // Clean up on component unmount
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        if (appStateSubscriptionRef.current) {
          appStateSubscriptionRef.current.remove();
        }
      };
    }
  }, [dispatch, timeout, signOut, user]);
};

export default useInactivityLogout;

